/**
 * 解析 Cookie 字符串为对象
 * @param {string} cookieString - Cookie 字符串
 * @returns {Object} Cookie 对象
 */
export function parseCookies(cookieString) {
  const cookies = {}
  if (!cookieString) return cookies
  
  cookieString.split(';').forEach(cookie => {
    const parts = cookie.split('=')
    if (parts.length < 2) return
    const key = parts.shift().trim()
    const value = decodeURIComponent(parts.join('='))
    cookies[key] = value
  })
  
  return cookies
}

/**
 * 获取指定 Cookie 的值（双端可用）
 * @param {Object} [req] - 服务端请求对象
 * @param {string} name - Cookie 名称
 * @returns {string|null} Cookie 值
 */
export const getCookie = (req, name) => {
  // 服务端
  if (req && req.headers.cookie) {
    const cookies = req.headers.cookie.split(';').map(c => c.trim())
    const cookie = cookies.find(c => c.startsWith(`${name}=`))
    if (cookie) {
      return cookie.split('=')[1]
    }
    return null
  }
  
  // 客户端
  if (typeof document !== 'undefined') {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }
  }
  
  return null
}

/**
 * 设置 Cookie（客户端可用）
 * @param {string} name - Cookie 名称
 * @param {string} value - Cookie 值
 * @param {Object} [options] - 配置选项
 * @param {number} [options.maxAge] - 最大有效期（秒）
 * @param {Date} [options.expires] - 过期时间
 * @param {string} [options.path] - 路径（默认 '/'）
 * @param {string} [options.domain] - 域名
 * @param {boolean} [options.secure] - 是否仅 HTTPS
 * @param {boolean} [options.httpOnly] - 是否仅 HTTP（客户端无法设置）
 * @param {'strict'|'lax'|'none'} [options.sameSite] - SameSite 策略
 */
export const setCookie = (name, value, options = {}) => {
  if (typeof document === 'undefined') return
  
  const defaultOptions = {
    path: '/',
    expires: new Date(Date.now() + 24 * 60 * 60 * 1000) // 默认1天
  }
  
  const opts = { ...defaultOptions, ...options }
  
  let cookie = `${name}=${value}`
  
  if (opts.expires) {
    if (typeof opts.expires === 'number') {
      opts.expires = new Date(Date.now() + opts.expires)
    }
    cookie += `; expires=${opts.expires.toUTCString()}`
  }
  
  if (opts.path) {
    cookie += `; path=${opts.path}`
  }
  
  if (opts.domain) {
    cookie += `; domain=${opts.domain}`
  }
  
  if (opts.secure) {
    cookie += '; secure'
  }
  
  if (opts.sameSite) {
    cookie += `; samesite=${opts.sameSite}`
  }
  
  document.cookie = cookie
}

/**
 * 删除 Cookie
 * @param {string} name - Cookie 名称
 * @param {Object} [options] - 配置选项
 * @param {string} [options.path] - 路径
 * @param {string} [options.domain] - 域名
 */
export const deleteCookie = (name, options = {}) => {
  const opts = { ...options, expires: new Date(0) }
  setCookie(name, '', opts)
}

/**
 * 服务端设置 Cookie 的响应头
 * @param {string} name - Cookie 名称
 * @param {string} value - Cookie 值
 * @param {Object} [options] - 配置选项
 * @returns {string} Set-Cookie 响应头字符串
 */
export function setServerCookie(name, value, options = {}) {
  const {
    maxAge,
    expires,
    path = '/',
    domain,
    secure = false,
    httpOnly = true,
    sameSite = 'lax'
  } = options

  let cookie = `${name}=${encodeURIComponent(value)}`

  if (path) cookie += `; Path=${path}`
  if (domain) cookie += `; Domain=${domain}`
  if (maxAge) cookie += `; Max-Age=${maxAge}`
  if (expires) cookie += `; Expires=${expires.toUTCString()}`
  if (secure) cookie += '; Secure'
  if (httpOnly) cookie += '; HttpOnly'
  
  // SameSite 处理
  if (sameSite) {
    cookie += `; SameSite=${sameSite}`
    // Chrome 80+ 要求设置 None 时必须同时设置 Secure
    if (sameSite.toLowerCase() === 'none' && !secure) {
      cookie += '; Secure'
    }
  }

  return cookie
}
